import React, { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
type Project = {
  id: string;
  name: string;
  scope: string;
  location: string;
  year: string;
  detail: string;
  image: string;
  imageAlt: string;
  specs: {
    label: string;
    value: string;
  }[];
};
const PROJECTS: Project[] = [{
  id: 'ridgeline',
  name: 'Ridgeline Residence',
  scope: 'Whole-home remodel',
  location: 'Private residence',
  year: '2025',
  detail: 'A dated floor plan opened into a single living, dining, and kitchen volume — new structural beam, white oak floors throughout, and a stone-clad fireplace wall with integrated millwork.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/4664c5d7-bd4b-49ca-ac8e-9692e8694fae.jpg',
  imageAlt: 'Remodeled living and dining area with a stone fireplace, oak floors, and custom built-in cabinetry',
  specs: [{
    label: 'Duration',
    value: '14 weeks'
  }, {
    label: 'Square footage',
    value: '3,200 sq ft'
  }, {
    label: 'Scope',
    value: 'Interior + finishes'
  }]
}, {
  id: 'orchard',
  name: 'Orchard Kitchen',
  scope: 'Kitchen & bath upgrade',
  location: 'Private residence',
  year: '2025',
  detail: 'Custom cabinetry to the ceiling, honed counters, and a full tile surround. Two baths were rebuilt in the same trade sequence to keep the household disruption to one window.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/ac0277d0-1248-4af9-ac9e-a566bcc10b9b.jpg',
  imageAlt: 'Remodeled kitchen with deep green floor-to-ceiling cabinetry, stone counters, and tile backsplash',
  specs: [{
    label: 'Duration',
    value: '7 weeks'
  }, {
    label: 'Cabinet runs',
    value: '32 linear ft'
  }, {
    label: 'Scope',
    value: 'Kitchen + 2 baths'
  }]
}, {
  id: 'creekside',
  name: 'Creekside Exterior',
  scope: 'Exterior improvements',
  location: 'Private residence',
  year: '2024',
  detail: 'Rot repair, new trim and siding, and a repainted exterior in a deep pine palette. Covered porch framing was rebuilt to correct long-standing drainage issues.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/fcb52abf-9209-4f9b-8ada-8f514fc62d97.jpg',
  imageAlt: 'Renovated home exterior with deep green siding, white trim, and a rebuilt covered porch',
  specs: [{
    label: 'Duration',
    value: '9 weeks'
  }, {
    label: 'Elevations',
    value: '4 full'
  }, {
    label: 'Scope',
    value: 'Exterior + paint'
  }]
}, {
  id: 'harborview',
  name: 'Harborview Built-Ins',
  scope: 'Cabinetry & millwork',
  location: 'Private residence',
  year: '2024',
  detail: 'Floor-to-ceiling library shelving, a concealed office nook, and paneled entry storage — all scribed to an out-of-square 1940s shell.',
  image: 'https://cdn.magicpatterns.com/patterns/generated-images/f8cd9869-9c64-4ce9-8c25-c9e6a2c1bd1c.jpg',
  imageAlt: 'Custom floor-to-ceiling library built-ins in dark green with brass hardware and integrated lighting',
  specs: [{
    label: 'Duration',
    value: '5 weeks'
  }, {
    label: 'Rooms',
    value: '3'
  }, {
    label: 'Scope',
    value: 'Custom millwork'
  }]
}];
export function WorkSection() {
  const [activeId, setActiveId] = useState(PROJECTS[0].id);
  const active = PROJECTS.find(p => p.id === activeId) ?? PROJECTS[0];
  return <section id="work" className="w-full bg-pine-950 py-24 sm:py-32" aria-labelledby="work-heading" data-id="element-227" data-mp="13nzigxxilpe9">
      <div className="mx-auto max-w-[1320px] px-6 lg:px-10" data-id="element-228" data-mp="1cinnqo33b732">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-229" data-mp="1cj5q82gcihjf">
          <div className="max-w-2xl" data-id="element-230" data-mp="1hgyu2ou6ezgj">
            <SectionLabel tone="light" data-id="element-231" data-mp="1mgjtf0d9d9ts">Selected Work</SectionLabel>
            <h2 id="work-heading" className="mt-7 font-display text-4xl font-light leading-[1.05] text-bone-50 sm:text-5xl" data-id="element-232" data-mp="1miei8prnhm35">
              Projects of all sizes, held to the same detail.
            </h2>
          </div>
          <a href="#contact" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest2 text-bone-100/60 transition-colors hover:text-bone-50" data-id="element-233" data-mp="2j8qdbpvvw63y">
            Discuss your project
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-234" data-mp="2syjoips46j7o" />
          </a>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-12" data-id="element-235" data-mp="258fn8p2eevju">
          <ul className="lg:col-span-7" role="list" data-id="element-236" data-mp="209w0imfzdfqf">
            {PROJECTS.map(project => {
            const isActive = project.id === active.id;
            return <li key={project.id} data-id="element-237" data-mp="2a7ig7b53tbbg">
                  <button type="button" onMouseEnter={() => setActiveId(project.id)} onFocus={() => setActiveId(project.id)} onClick={() => setActiveId(project.id)} aria-pressed={isActive} className="group w-full border-t border-white/10 py-8 text-left last:border-b" data-id="element-238" data-mp="2a6h4d4g9kkso">
                    <div className="flex items-center gap-6" data-id="element-239" data-mp="2188mp9rs6yc">
                      <img src={project.image} alt="" aria-hidden="true" className={`hidden h-16 w-24 shrink-0 rounded-md object-cover transition-opacity duration-300 sm:block ${isActive ? 'opacity-100' : 'opacity-40'}`} data-id="element-240" data-mp="3j8abthgsn8y1" />
                      <div className="min-w-0 flex-1" data-id="element-241" data-mp="61igk7h4h7fu">
                        <div className="flex items-baseline justify-between gap-6" data-id="element-242" data-mp="60yx2mbtqazp">
                          <span className={`font-display text-3xl font-light transition-colors duration-300 sm:text-4xl ${isActive ? 'text-bone-50' : 'text-bone-100/45'}`} data-id="element-243" data-mp="ayqx63mmh3ez">
                            {project.name}
                          </span>
                          <span className="shrink-0 font-mono text-[11px] tracking-wider text-bone-100/40" data-id="element-244" data-mp="18d0dybbk42jh">
                            {project.year}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.16em] text-bone-100/40" data-id="element-245" data-mp="zbsdpnphcrmr">
                          <span data-id="element-246" data-mp="uflhwnqsswij">{project.scope}</span>
                          <span aria-hidden="true" data-id="element-247" data-mp="plzc9fj1xsw8">·</span>
                          <span data-id="element-248" data-mp="knxm6an7ktb9">{project.location}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                </li>;
          })}
          </ul>

          <aside className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start" aria-live="polite" data-id="element-249" data-mp="bniy53iv1042">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-pine-900" data-id="element-250" data-mp="6ooczuxf6zgs">
              <img key={active.image} src={active.image} alt={active.imageAlt} className="aspect-[4/3] w-full object-cover" data-id="element-251" data-mp="2ib01m0bxo4kf" />
              <div className="p-8" data-id="element-252" data-mp="2sa7hova1ydb8">
              <p className="text-[11px] uppercase tracking-widest2 text-pine-200" data-id="element-253" data-mp="2wbash3d8tj5l">
                {active.scope}
              </p>
              <h3 className="mt-5 font-display text-3xl font-light text-bone-50" data-id="element-254" data-mp="1zlthc8vhr4wi">
                {active.name}
              </h3>
              <p className="mt-5 text-sm leading-relaxed text-bone-100/70" data-id="element-255" data-mp="24kmx67y937zu">
                {active.detail}
              </p>
              <dl className="mt-8 space-y-4 border-t border-white/10 pt-6" data-id="element-256" data-mp="28jvmn0m9cjpl">
                {active.specs.map(spec => <div key={spec.label} className="flex items-baseline justify-between gap-4" data-id="element-257" data-mp="1gv2abdyz7qla">
                    <dt className="text-[11px] uppercase tracking-[0.16em] text-bone-100/45" data-id="element-258" data-mp="1kub5mn9nzwiy">
                      {spec.label}
                    </dt>
                    <dd className="text-sm text-bone-50" data-id="element-259" data-mp="9aiadgyuzzaw">{spec.value}</dd>
                  </div>)}
              </dl>
              </div>
            </div>
            <p className="mt-4 px-2 text-xs leading-relaxed text-bone-100/40" data-id="element-260" data-mp="j7wech3livmb">
              Full photo galleries are shared during consultation, along with
              references for comparable projects.
            </p>
          </aside>
        </div>
      </div>
    </section>;
}